import {
  Fund,
  FundOverlapFund,
  FundOverlapMatrixResponse,
} from "creso-core/types";
import { useFundOverlapMatrix } from "creso-core/hooks";
import { ChevronDown } from "lucide-react";
import {
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "web-design-system";

export function FundOverlap({
  funds,
  fundIds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  fundIds: string[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  const { data } = useFundOverlapMatrix(fundIds);

  if (!data) return null;

  const { overlap_matrix } = data as FundOverlapMatrixResponse;
  const n = funds.length;

  if (n < 2) return null;

  // Rows: funds[n-1] down to funds[1] — lower triangle shown in descending order
  const rowIndices = Array.from({ length: n - 1 }, (_, i) => n - 1 - i);

  return (
    <div className="flex flex-col break-inside-avoid">
      <Collapsible
        defaultOpen
        className="w-full bg-muted rounded-lg overflow-hidden border border-border group"
      >
        <CollapsibleTrigger className="flex w-full justify-between items-center px-5 py-4">
          <div
            className="flex items-center shrink-0"
            style={{ width: `${labelWidth}px` }}
          >
            <span className="font-semibold text-base text-left">
              Portfolio overlap
            </span>
          </div>
          {variant === "default" ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
          ) : null}
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="flex w-full bg-card border-b border-border items-stretch">
            <div style={{ width: `${labelWidth}px` }} />
            {data?.funds?.map((fund: FundOverlapFund) => (
              <div
                key={fund.id}
                className="flex flex-1 items-center px-4 py-3 text-sm text-left"
              >
                <span className="line-clamp-2">
                  {funds.find((f) => f.fund_id === fund.id)?.legal_name}
                </span>
              </div>
            ))}
            <div
              className={cn(
                "w-[80px] h-16 shrink-0 bg-muted",
                variant === "report" && "w-0"
              )}
            />
          </div>
          {rowIndices.map((rowIdx) => (
            <div
              key={rowIdx}
              className="flex w-full border-b border-border last:border-0 bg-background items-stretch"
            >
              <div
                className="flex items-center px-4 py-3 bg-card text-sm shrink-0"
                style={{ width: `${labelWidth}px` }}
              >
                <span className="line-clamp-2">
                  {
                    funds.find((f) => f.fund_id === data.funds[rowIdx].id)
                      ?.legal_name
                  }
                </span>
              </div>

              {Array.from({ length: n }).map((_, colIdx) => {
                const showValue = colIdx < rowIdx;
                const pct = showValue ? overlap_matrix[rowIdx][colIdx] : null;

                return (
                  <div
                    key={colIdx}
                    className={`flex flex-1 items-center px-4 py-3 ${
                      colIdx < n - 1 ? "border-r border-border" : ""
                    } ${!showValue ? "bg-muted" : ""}`}
                  >
                    {showValue && pct !== null ? (
                      <div className="flex flex-col gap-1.5 w-full">
                        <span className="text-sm font-semibold">
                          {pct.toFixed(2)}%
                        </span>
                        <div className="h-2 w-full rounded bg-secondary overflow-hidden">
                          <div
                            className={cn("h-full rounded-full", {
                              "bg-destructive": true,
                              "bg-chart-3": pct < 50,
                              "bg-constructive": pct < 20,
                            })}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              <div
                className={cn(
                  "w-[80px] h-16 shrink-0 bg-muted",
                  variant === "report" && "w-0"
                )}
              />
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
